import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Calendar, Zap, Tag, Trash2, Check, Plus, Sparkles, Loader2,
  MessageSquare, Activity as ActivityIcon, FileText, Save, Mic, Wand2,
  Copy, Link2, Heart, Settings, ArrowRight, RotateCcw, AlertTriangle, Circle,
  Paperclip, Eye, File, Edit, CheckSquare, GripVertical,
} from "lucide-react";
import clsx from "clsx";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Card, Comment, Activity, Priority, Mood, Attachment, Subtask } from "../../types";
import IagoSpinner from "../iago/IagoSpinner";
import { priorityMeta, categoryBg, categoryBorder, fmtRelative, moodMeta } from "../../lib/utils";
import { formatActivity } from "../../lib/activityFormat";
import { api } from "../../lib/api";
import { useStore } from "../../stores/store";
import { AttachmentViewer } from "./AttachmentViewer";
import { confirmAsync } from "../../stores/dialogStore";

interface Props {
  card: Card | null;
  onClose: () => void;
}

type Tab = "details" | "comments" | "activity" | "transcript";

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Plus, ArrowRight, Check, RotateCcw, FileText, Edit,
  AlertTriangle, Calendar, Copy, MessageSquare, Sparkles,
  Wand2, Settings, Heart, CheckSquare, Circle, Paperclip,
};

interface SortableSubtaskProps {
  id: string;
  subtask: Subtask;
  index: number;
  onToggle: (i: number) => void;
  onChange: (i: number, title: string) => void;
  onRemove: (i: number) => void;
}

function SortableSubtask({ id, subtask, index, onToggle, onChange, onRemove }: SortableSubtaskProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={clsx(
        "group flex items-center gap-1 rounded-md px-1 py-1 hover:bg-white/[0.03]",
        isDragging && "z-10 bg-white/[0.06] ring-1 ring-violet-500/40",
      )}
    >
      <button
        {...attributes}
        {...listeners}
        type="button"
        aria-label="Arrastar subtarefa"
        className="shrink-0 cursor-grab touch-none rounded p-0.5 text-slate-500 opacity-30 transition hover:bg-white/5 hover:text-slate-200 group-hover:opacity-100 active:cursor-grabbing"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={() => onToggle(index)}
        className={clsx(
          "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition",
          subtask.done
            ? "border-emerald-500 bg-emerald-500/80 text-white"
            : "border-white/20 hover:border-violet-400",
        )}
      >
        {subtask.done && <Check className="h-3 w-3" />}
      </button>
      <input
        value={subtask.title}
        onChange={(e) => onChange(index, e.target.value)}
        className={clsx(
          "flex-1 bg-transparent text-sm outline-none",
          subtask.done && "text-slate-500 line-through",
        )}
      />
      <button
        onClick={() => onRemove(index)}
        className="rounded p-1 text-slate-500 opacity-0 transition hover:bg-white/5 hover:text-rose-300 group-hover:opacity-100"
      >
        <X className="h-3 w-3" />
      </button>
    </li>
  );
}

export function CardModal({ card, onClose }: Props) {
  const [draft, setDraft] = useState<Card | null>(card);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<Tab>("details");
  const [comments, setComments] = useState<Comment[]>([]);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [newComment, setNewComment] = useState("");
  const [newSubtask, setNewSubtask] = useState("");
  const [editingDesc, setEditingDesc] = useState(false);
  const [originalDesc, setOriginalDesc] = useState("");
  const [savingDesc, setSavingDesc] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingWhy, setEditingWhy] = useState(false);
  const [refineOpen, setRefineOpen] = useState(false);
  const [refineInstr, setRefineInstr] = useState("");
  const [refining, setRefining] = useState(false);
  const [newResourceTitle, setNewResourceTitle] = useState("");
  const [newResourceUrl, setNewResourceUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [viewingAttachment, setViewingAttachment] = useState<Attachment | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const subtaskIdsRef = useRef<string[]>([]);
  const subtaskIdSeqRef = useRef(0);
  const subtaskIds = useMemo(() => {
    const current = subtaskIdsRef.current;
    const target = draft?.subtasks.length ?? 0;
    if (current.length === target) return current;
    const next = current.slice(0, target);
    while (next.length < target) {
      subtaskIdSeqRef.current += 1;
      next.push(`subtask-${subtaskIdSeqRef.current}`);
    }
    subtaskIdsRef.current = next;
    return next;
  }, [draft?.subtasks.length]);

  const subtaskSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const lanes = useStore((s) => s.lanes);
  const categories = useStore((s) => s.categories);
  const setSettingsOpen = useStore((s) => s.setSettingsOpen);
  const upsertCard = useStore((s) => s.upsertCard);
  const deleteCard = useStore((s) => s.deleteCard);
  const duplicateCard = useStore((s) => s.duplicateCard);
  const setActiveCard = useStore((s) => s.setActiveCard);

  useEffect(() => {
    setDraft(card);
    setDirty(false);
    setTab("details");
    subtaskIdsRef.current = [];
    if (card) {
      api.listComments(card.id).then(setComments).catch(() => setComments([]));
      api.activity(card.id).then(setActivity).catch(() => setActivity([]));
    }
  }, [card?.id]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!card) return;
      const editing = editingTitle || editingNotes || editingWhy || refineOpen;
      if (e.key === "Escape") {
        if (editingDesc) { cancelDescription(); return; }
        if (!editing) closeWithSave();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        save();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "d" && !editing) {
        e.preventDefault();
        doDuplicate();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [card, draft, dirty, editingDesc, editingTitle, editingNotes, editingWhy, refineOpen]);

  if (!card || !draft) return null;

  function patch(p: Partial<Card>) {
    setDraft((d) => (d ? { ...d, ...p } : d));
    setDirty(true);
  }

  async function saveDescription() {
    if (!draft) return;
    setSavingDesc(true);
    try {
      const updated = await api.updateCard(draft.id, { description: draft.description });
      upsertCard(updated);
      setDraft(updated);
      setDirty(false);
      setEditingDesc(false);
    } finally {
      setSavingDesc(false);
    }
  }

  function cancelDescription() {
    setDraft((d) => (d ? { ...d, description: originalDesc } : d));
    setEditingDesc(false);
    setDirty(false);
  }

  async function save() {
    if (!draft || !dirty) return;
    setSaving(true);
    try {
      const updated = await api.updateCard(draft.id, {
        title: draft.title,
        description: draft.description,
        priority: draft.priority,
        effort: draft.effort,
        dueDate: draft.dueDate,
        categoryIds: draft.categoryIds,
        subtasks: draft.subtasks,
        laneId: draft.laneId,
        mood: draft.mood,
        whyItMatters: draft.whyItMatters,
        notes: draft.notes,
        resources: draft.resources,
      });
      upsertCard(updated);
      setDraft(updated);
      setDirty(false);
    } finally {
      setSaving(false);
    }
  }

  async function closeWithSave() {
    if (dirty) {
      try {
        await save();
      } catch {
        // se falhar salvar, ainda assim fecha pra não prender o user
      }
    }
    onClose();
  }

  function toggleSubtask(idx: number) {
    const subtasks = draft!.subtasks.map((s, i) => (i === idx ? { ...s, done: !s.done } : s));
    patch({ subtasks });
  }

  function addSubtask() {
    if (!newSubtask.trim()) return;
    patch({ subtasks: [...draft!.subtasks, { title: newSubtask.trim(), done: false }] });
    setNewSubtask("");
  }

  function removeSubtask(idx: number) {
    patch({ subtasks: draft!.subtasks.filter((_, i) => i !== idx) });
  }

  function updateSubtask(idx: number, title: string) {
    const subtasks = draft!.subtasks.map((s, i) => (i === idx ? { ...s, title } : s));
    patch({ subtasks });
  }

  function onSubtaskDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id || !draft) return;
    const oldIndex = subtaskIds.indexOf(String(active.id));
    const newIndex = subtaskIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    subtaskIdsRef.current = arrayMove(subtaskIds, oldIndex, newIndex);
    patch({ subtasks: arrayMove(draft.subtasks, oldIndex, newIndex) });
  }

  function toggleCategory(id: number) {
    const list = draft!.categoryIds || [];
    const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    patch({ categoryIds: next });
  }

  function addResource() {
    const title = newResourceTitle.trim();
    const url = newResourceUrl.trim();
    if (!title || !url) return;
    patch({ resources: [...(draft!.resources || []), { title, url }] });
    setNewResourceTitle("");
    setNewResourceUrl("");
  }

  function removeResource(idx: number) {
    patch({ resources: draft!.resources.filter((_, i) => i !== idx) });
  }

  async function handleAttachmentUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !draft) return;

    if (file.size > 10 * 1024 * 1024) {
      await confirmAsync({
        title: "Arquivo muito grande",
        description: "O limite maximo e de 10 MB por arquivo.",
        confirmLabel: "OK",
        cancelLabel: "",
      });
      return;
    }

    if ((draft.attachments || []).length >= 10) {
      await confirmAsync({
        title: "Limite de anexos",
        description: "Maximo de 10 anexos por card.",
        confirmLabel: "OK",
        cancelLabel: "",
      });
      return;
    }

    setUploading(true);
    try {
      const att = await api.uploadAttachment(draft.id, file);
      const updatedList = [...(draft.attachments || []), att];
      setDraft((d) => (d ? { ...d, attachments: updatedList } : d));
      upsertCard({ ...draft, attachments: updatedList });
    } catch (err: any) {
      await confirmAsync({
        title: "Erro ao enviar",
        description: err.message || "Nao foi possivel enviar o anexo.",
        confirmLabel: "OK",
        cancelLabel: "",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDeleteAttachment(attachmentId: string) {
    if (!draft) return;
    try {
      await api.deleteAttachment(attachmentId);
      const updatedList = (draft.attachments || []).filter((a) => a.id !== attachmentId);
      setDraft((d) => (d ? { ...d, attachments: updatedList } : d));
    } catch (err: any) {
      await confirmAsync({
        title: "Erro ao remover",
        description: err.message || "Nao foi possivel remover o anexo.",
        confirmLabel: "OK",
        cancelLabel: "",
      });
    }
  }

  async function doDelete() {
    const ok = await confirmAsync({
      title: "Remover este card?",
      description: "A ação é permanente e não pode ser desfeita.",
      confirmLabel: "Remover",
      danger: true,
    });
    if (!ok) return;
    await deleteCard(draft!.id);
    onClose();
  }

  async function doDuplicate() {
    const dup = await duplicateCard(draft!.id);
    setActiveCard(dup.id);
  }

  async function postComment() {
    if (!newComment.trim()) return;
    const c = await api.addComment(draft!.id, newComment.trim());
    setComments((prev) => [...prev, c]);
    setNewComment("");
  }

  async function runRefine() {
    if (!refineInstr.trim()) return;
    setRefining(true);
    try {
      const updated = await api.refineCard(draft!.id, refineInstr.trim());
      setDraft(updated);
      upsertCard(updated);
      setRefineOpen(false);
      setRefineInstr("");
      const act = await api.activity(updated.id);
      setActivity(act);
    } catch (e: any) {
      await confirmAsync({
        title: "Falha ao refinar",
        description: e.message,
        confirmLabel: "OK",
        cancelLabel: "",
      });
    } finally {
      setRefining(false);
    }
  }

  const pm = priorityMeta[draft.priority] ?? priorityMeta.medium;
  const mm = moodMeta[draft.mood] ?? moodMeta.routine;
  const progress =
    draft.subtasks.length > 0
      ? Math.round((draft.subtasks.filter((s) => s.done).length / draft.subtasks.length) * 100)
      : 0;

  return (
    <>
    <AnimatePresence>
      <motion.div
        key="overlay"
        className="fixed inset-0 z-40 flex items-center justify-center md:p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.12 }}
      >
        <div className="absolute inset-0 bg-black/65 backdrop-blur-md" onClick={closeWithSave} />
        <motion.div
          className="glass relative flex h-full w-full flex-col overflow-hidden rounded-none border-l-0 shadow-elevated md:h-[90vh] md:max-w-[1100px] md:rounded-2xl md:border-l-[4px]"
          style={{ borderLeftColor: mm.border }}
          initial={{ scale: 0.96, y: 12, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.97, y: 6, opacity: 0 }}
          transition={{ type: "spring", damping: 24, stiffness: 280 }}
        >
          {/* Mood tint no topo */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-32 opacity-[0.18]"
            style={{
              background: `radial-gradient(800px 200px at 20% 0%, ${mm.border}, transparent 70%)`,
            }}
          />

          {/* Header */}
          <div className="relative flex items-start justify-between gap-3 border-b border-white/5 px-6 py-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-400">
                <span>#{draft.id}</span>
                <span>·</span>
                <select
                  value={draft.laneId}
                  onChange={(e) => patch({ laneId: Number(e.target.value) })}
                  className="cursor-pointer rounded border border-white/10 bg-transparent px-1.5 py-0.5 text-[11px] text-slate-200 focus:outline-none"
                >
                  {lanes.map((l) => (
                    <option key={l.id} value={l.id} className="bg-bg-2">
                      {l.title}
                    </option>
                  ))}
                </select>
                {(draft.transcript || draft.aiGenerated) && (
                  <>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1 text-violet-300">
                      <Sparkles className="h-3 w-3" /> IA
                    </span>
                  </>
                )}
              </div>
              {editingTitle ? (
                <input
                  autoFocus
                  value={draft.title}
                  onChange={(e) => patch({ title: e.target.value })}
                  onBlur={() => setEditingTitle(false)}
                  onKeyDown={(e) => e.key === "Enter" && setEditingTitle(false)}
                  className="mt-1 w-full rounded bg-white/5 px-2 py-1 text-xl font-semibold text-slate-100 outline-none ring-1 ring-violet-500/60"
                />
              ) : (
                <h1
                  onClick={() => setEditingTitle(true)}
                  className="mt-1 cursor-text break-words text-xl font-semibold text-slate-100 hover:bg-white/[0.03] rounded px-1 -mx-1"
                >
                  {draft.title}
                </h1>
              )}
              {(draft.whyItMatters || editingWhy) && (
                <div className="mt-2">
                  {editingWhy ? (
                    <textarea
                      autoFocus
                      value={draft.whyItMatters}
                      onChange={(e) => patch({ whyItMatters: e.target.value })}
                      onBlur={() => setEditingWhy(false)}
                      maxLength={240}
                      className="w-full resize-none rounded bg-white/5 px-2 py-1 text-xs italic text-violet-200 outline-none ring-1 ring-violet-500/60"
                      rows={2}
                      placeholder="Por que essa task importa?"
                    />
                  ) : (
                    <p
                      onClick={() => setEditingWhy(true)}
                      className="cursor-text rounded px-1 -mx-1 text-xs italic text-violet-200/90 hover:bg-white/[0.03]"
                    >
                      <Heart className="mr-1 inline h-3 w-3" /> {draft.whyItMatters}
                    </p>
                  )}
                </div>
              )}
              {!draft.whyItMatters && !editingWhy && (
                <button
                  onClick={() => setEditingWhy(true)}
                  className="mt-1 text-xs text-slate-500 hover:text-violet-300"
                >
                  + por que essa task importa?
                </button>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                onClick={doDuplicate}
                className="rounded-lg p-1.5 text-slate-400 transition hover:bg-white/5 hover:text-slate-100"
                title="Duplicar (⌘D)"
              >
                <Copy className="h-4 w-4" />
              </button>
              <button
                onClick={() => setRefineOpen(true)}
                className="flex items-center gap-1.5 rounded-lg border border-violet-500/40 bg-violet-500/10 px-2.5 py-1.5 text-xs font-medium text-violet-200 transition hover:bg-violet-500/20"
                title="Refinar com IA"
              >
                <Wand2 className="h-3.5 w-3.5" /> Refinar
              </button>
              <button
                onClick={save}
                disabled={!dirty || saving}
                className={clsx(
                  "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition",
                  dirty
                    ? "bg-violet-500 text-white hover:bg-violet-400"
                    : "bg-white/5 text-slate-400",
                )}
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                {saving ? "Salvando…" : dirty ? "Salvar" : "Salvo"}
              </button>
              <button
                onClick={doDelete}
                className="rounded-lg p-1.5 text-slate-400 transition hover:bg-rose-500/10 hover:text-rose-300"
                title="Remover card"
              >
                <Trash2 className="h-4 w-4" />
              </button>
              <button
                onClick={closeWithSave}
                className="rounded-lg p-1.5 text-slate-400 transition hover:bg-white/5 hover:text-slate-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex flex-1 overflow-hidden">
            {/* Main */}
            <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-5">
              <div className="mb-4 flex items-center gap-1 border-b border-white/5">
                {(
                  [
                    { key: "details", label: "Detalhes", icon: FileText },
                    { key: "comments", label: `Comentários (${comments.length})`, icon: MessageSquare },
                    { key: "activity", label: "Atividade", icon: ActivityIcon },
                    ...(draft.transcript ? [{ key: "transcript" as Tab, label: "Transcript", icon: Mic }] : []),
                  ] as const
                ).map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => setTab(key)}
                    className={clsx(
                      "flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition",
                      tab === key
                        ? "border-violet-500 text-slate-100"
                        : "border-transparent text-slate-400 hover:text-slate-200",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
              </div>

              {tab === "details" && (
                <div className="space-y-6">
                  <section>
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                        Descricao
                      </h3>
                      {!editingDesc && (
                        <button
                          onClick={() => {
                            setOriginalDesc(draft.description);
                            setEditingDesc(true);
                          }}
                          className="text-xs text-slate-400 hover:text-slate-200"
                        >
                          Editar
                        </button>
                      )}
                    </div>
                    {editingDesc ? (
                      <div className="space-y-2">
                        <textarea
                          value={draft.description}
                          onChange={(e) => patch({ description: e.target.value })}
                          className="min-h-[240px] w-full resize-y rounded-lg border border-white/10 bg-black/30 p-3 font-mono text-[13px] text-slate-100 focus:border-violet-500/60 focus:outline-none"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            onClick={saveDescription}
                            disabled={savingDesc}
                            className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-500 disabled:opacity-50 transition"
                          >
                            {savingDesc ? "Salvando..." : "Salvar"}
                          </button>
                          <button
                            onClick={cancelDescription}
                            className="rounded-md bg-white/5 px-3 py-1.5 text-xs text-slate-300 hover:bg-white/10 transition"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : draft.description.trim() ? (
                      <div className="prose-invert rounded-lg bg-white/[0.02] p-4">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>{draft.description}</ReactMarkdown>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setOriginalDesc(draft.description);
                          setEditingDesc(true);
                        }}
                        className="w-full rounded-lg border border-dashed border-white/10 p-4 text-left text-sm text-slate-500 hover:border-violet-500/40"
                      >
                        Adicionar descricao (markdown suportado)...
                      </button>
                    )}
                  </section>

                  <section>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Subtarefas{" "}
                      {draft.subtasks.length > 0 && (
                        <span className="ml-1 font-mono text-slate-500">
                          {draft.subtasks.filter((s) => s.done).length}/{draft.subtasks.length} · {progress}%
                        </span>
                      )}
                    </h3>
                    {draft.subtasks.length > 0 && (
                      <div className="mb-3 h-1 overflow-hidden rounded-full bg-white/5">
                        <motion.div
                          className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-400"
                          initial={false}
                          animate={{ width: `${progress}%` }}
                          transition={{ duration: 0.3 }}
                        />
                      </div>
                    )}
                    <DndContext
                      sensors={subtaskSensors}
                      collisionDetection={closestCenter}
                      onDragEnd={onSubtaskDragEnd}
                    >
                      <SortableContext items={subtaskIds} strategy={verticalListSortingStrategy}>
                        <ul className="space-y-1">
                          {draft.subtasks.map((s, i) => (
                            <SortableSubtask
                              key={subtaskIds[i]}
                              id={subtaskIds[i]}
                              subtask={s}
                              index={i}
                              onToggle={toggleSubtask}
                              onChange={updateSubtask}
                              onRemove={removeSubtask}
                            />
                          ))}
                        </ul>
                      </SortableContext>
                    </DndContext>
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        value={newSubtask}
                        onChange={(e) => setNewSubtask(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addSubtask()}
                        placeholder="Nova subtarefa…"
                        className="flex-1 rounded-md border border-white/10 bg-black/20 px-3 py-1.5 text-sm focus:border-violet-500/60 focus:outline-none"
                      />
                      <button
                        onClick={addSubtask}
                        disabled={!newSubtask.trim()}
                        className="rounded-md bg-white/5 p-1.5 text-slate-300 transition hover:bg-white/10 disabled:opacity-30"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </section>

                  <section>
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                        Notas
                      </h3>
                      <button
                        onClick={() => setEditingNotes((v) => !v)}
                        className="text-xs text-slate-400 hover:text-slate-200"
                      >
                        {editingNotes ? "Visualizar" : "Editar"}
                      </button>
                    </div>
                    {editingNotes ? (
                      <textarea
                        value={draft.notes}
                        onChange={(e) => patch({ notes: e.target.value })}
                        className="min-h-[120px] w-full resize-y rounded-lg border border-white/10 bg-black/30 p-3 font-mono text-[13px] text-slate-100 focus:border-violet-500/60 focus:outline-none"
                        placeholder="Notas livres (markdown)…"
                      />
                    ) : draft.notes?.trim() ? (
                      <div className="prose-invert rounded-lg bg-white/[0.02] p-4">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>{draft.notes}</ReactMarkdown>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditingNotes(true)}
                        className="w-full rounded-lg border border-dashed border-white/10 p-3 text-left text-xs text-slate-500 hover:border-violet-500/40"
                      >
                        Adicionar notas livres (ideias, contexto, links rápidos)…
                      </button>
                    )}
                  </section>

                  <section>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                      <Link2 className="mr-1 inline h-3 w-3" /> Referências
                    </h3>
                    <ul className="space-y-1">
                      {(draft.resources || []).map((r, i) => (
                        <li
                          key={i}
                          className="group flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] px-3 py-1.5"
                        >
                          <Link2 className="h-3 w-3 shrink-0 text-slate-400" />
                          <a
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 truncate text-sm text-cyan-300 hover:underline"
                          >
                            {r.title || r.url}
                          </a>
                          <span className="hidden text-[10px] text-slate-500 md:inline">{r.url}</span>
                          <button
                            onClick={() => removeResource(i)}
                            className="rounded p-0.5 text-slate-500 opacity-0 hover:bg-white/5 hover:text-rose-300 group-hover:opacity-100"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        value={newResourceTitle}
                        onChange={(e) => setNewResourceTitle(e.target.value)}
                        placeholder="Título"
                        className="w-40 rounded-md border border-white/10 bg-black/20 px-2 py-1 text-xs focus:border-violet-500/60 focus:outline-none"
                      />
                      <input
                        value={newResourceUrl}
                        onChange={(e) => setNewResourceUrl(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addResource()}
                        placeholder="https://…"
                        className="flex-1 rounded-md border border-white/10 bg-black/20 px-2 py-1 text-xs focus:border-violet-500/60 focus:outline-none"
                      />
                      <button
                        onClick={addResource}
                        disabled={!newResourceTitle.trim() || !newResourceUrl.trim()}
                        className="rounded-md bg-white/5 p-1 text-slate-300 hover:bg-white/10 disabled:opacity-30"
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </section>

                  {draft.aiReasoning && (
                    <section className="rounded-lg border border-violet-500/20 bg-violet-500/[0.04] p-3 text-xs text-slate-300">
                      <div className="mb-1 flex items-center gap-1.5 font-semibold text-violet-300">
                        <Sparkles className="h-3 w-3" /> Raciocínio da IA
                      </div>
                      <p className="italic">{draft.aiReasoning}</p>
                    </section>
                  )}

                  <section>
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                        <Paperclip className="mr-1 inline h-3 w-3" /> Anexos{" "}
                        {(draft.attachments || []).length > 0 && (
                          <span className="ml-1 font-mono text-slate-500">({draft.attachments.length}/10)</span>
                        )}
                      </h3>
                      <label className="cursor-pointer rounded-md bg-white/5 px-2 py-1 text-xs text-slate-300 hover:bg-white/10 transition">
                        {uploading ? "Enviando..." : "Upload"}
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          onChange={handleAttachmentUpload}
                          accept="image/*,video/*,audio/*,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx"
                        />
                      </label>
                    </div>
                    {(draft.attachments || []).length === 0 && (
                      <p className="rounded-lg border border-dashed border-white/10 p-3 text-center text-xs text-slate-500">
                        Nenhum anexo ainda. Maximo 10 MB por arquivo.
                      </p>
                    )}
                    {(draft.attachments || []).length > 0 && (
                      <ul className="space-y-1">
                        {(draft.attachments || []).map((att) => (
                          <li
                            key={att.id}
                            className="group flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] px-3 py-1.5"
                          >
                            <File className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                            <span className="flex-1 truncate text-sm text-slate-300">{att.name}</span>
                            <span className="text-[10px] text-slate-500">{(att.size / 1024).toFixed(0)} KB</span>
                            <button
                              onClick={() => setViewingAttachment(att)}
                              className="rounded p-0.5 text-slate-500 hover:bg-white/5 hover:text-cyan-300"
                              title="Visualizar"
                            >
                              <Eye className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => handleDeleteAttachment(att.id)}
                              className="rounded p-0.5 text-slate-500 opacity-0 hover:bg-white/5 hover:text-rose-300 group-hover:opacity-100"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                </div>
              )}

              {tab === "comments" && (
                <div className="space-y-3">
                  {comments.length === 0 && (
                    <p className="rounded-lg bg-white/[0.02] p-4 text-center text-sm text-slate-500">
                      Nada por aqui ainda.
                    </p>
                  )}
                  {comments.map((c) => (
                    <div key={c.id} className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
                      <div className="mb-1 text-[11px] text-slate-500">{fmtRelative(c.createdAt)}</div>
                      <div className="prose-invert text-sm">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>{c.body}</ReactMarkdown>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-start gap-2">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Escreva um comentário… (markdown)"
                      className="min-h-[80px] flex-1 resize-y rounded-lg border border-white/10 bg-black/30 p-3 text-sm focus:border-violet-500/60 focus:outline-none"
                    />
                    <button
                      onClick={postComment}
                      disabled={!newComment.trim()}
                      className="rounded-lg bg-violet-500 px-3 py-2 text-sm font-medium text-white transition hover:bg-violet-400 disabled:opacity-40"
                    >
                      Comentar
                    </button>
                  </div>
                </div>
              )}

              {tab === "activity" && (
                <div className="relative pl-6">
                  {/* linha vertical da timeline */}
                  <div className="absolute left-[11px] top-2 bottom-2 w-px bg-white/[0.06]" />
                  {activity.length === 0 && (
                    <p className="rounded-lg bg-white/[0.02] p-4 text-center text-sm text-slate-500">
                      Esse card ainda nao tem historico.
                    </p>
                  )}
                  {activity.map((a, i) => {
                    const laneById = Object.fromEntries(lanes.map((l) => [l.id, l.title]));
                    const fa = formatActivity(a, laneById);
                    const Icon = ICON_MAP[fa.icon] || Circle;
                    const prevDate = i > 0 ? activity[i - 1].createdAt.slice(0, 10) : null;
                    const thisDate = a.createdAt.slice(0, 10);
                    const showDateSep = thisDate !== prevDate;
                    return (
                      <div key={a.id}>
                        {showDateSep && (
                          <div className="mb-2 mt-3 text-[10px] font-medium uppercase tracking-wider text-slate-500">
                            {fmtRelative(a.createdAt)}
                          </div>
                        )}
                        <div className="group relative flex items-start gap-3 pb-2">
                          {/* bolinha na timeline */}
                          <div className={`absolute -left-[19px] top-1.5 h-[7px] w-[7px] rounded-full border-2 border-black/80 bg-current ${fa.color}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-[12px] leading-relaxed ${fa.color}`}>
                              <Icon className="mr-1 inline h-3 w-3" />
                              {fa.message}
                            </p>
                          </div>
                          <span className="shrink-0 text-[10px] text-slate-500">
                            {new Date(a.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {tab === "transcript" && draft.transcript && (
                <div className="rounded-lg border border-white/5 bg-black/30 p-4 font-mono text-sm italic text-slate-300">
                  "{draft.transcript}"
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="hidden w-64 shrink-0 flex-col gap-5 overflow-y-auto border-l border-white/5 bg-white/[0.015] px-5 py-5 lg:flex scrollbar-thin">
              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  Mood
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {(["urgent", "exploratory", "routine", "creative"] as Mood[]).map((m) => {
                    const meta = moodMeta[m];
                    const active = draft.mood === m;
                    return (
                      <button
                        key={m}
                        onClick={() => patch({ mood: m })}
                        className={clsx(
                          "rounded-md px-2 py-1.5 text-xs font-medium transition",
                          active ? "ring-2" : "ring-1 ring-white/10 opacity-60 hover:opacity-100",
                        )}
                        style={{
                          backgroundColor: meta.bg,
                          borderColor: active ? meta.border : undefined,
                          color: meta.border,
                          boxShadow: active ? `0 0 0 1px ${meta.border}` : undefined,
                        }}
                      >
                        {meta.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  Prioridade
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {(["low", "medium", "high", "urgent"] as Priority[]).map((p) => {
                    const m = priorityMeta[p];
                    const active = draft.priority === p;
                    return (
                      <button
                        key={p}
                        onClick={() => patch({ priority: p })}
                        className={clsx(
                          "rounded-md px-2 py-1.5 text-xs font-medium transition ring-1 ring-inset",
                          active
                            ? `${m.glow} ${m.color} ${m.ring}`
                            : "bg-white/[0.02] text-slate-400 ring-white/10 hover:bg-white/5",
                        )}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  <Zap className="h-3 w-3" /> Complexidade
                </div>
                <div className="flex flex-wrap gap-1">
                  {["Trivial", "Fácil", "Regular", "Difícil", "Complexa"].map((v) => (
                    <button
                      key={v}
                      onClick={() => patch({ effort: draft.effort === v ? null : v })}
                      className={clsx(
                        "rounded-md px-2 py-1 text-xs transition ring-1 ring-inset",
                        draft.effort === v
                          ? "bg-violet-500/25 text-violet-200 ring-violet-500/50"
                          : "bg-white/[0.02] text-slate-400 ring-white/10 hover:bg-white/5",
                      )}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  <Calendar className="h-3 w-3" /> Data limite
                </div>
                <input
                  type="date"
                  value={draft.dueDate ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) { patch({ dueDate: null }); return; }
                    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
                    if (m) {
                      const picked = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
                      const today = new Date(); today.setHours(0,0,0,0);
                      const oneYearAgo = new Date(today); oneYearAgo.setFullYear(today.getFullYear() - 1);
                      if (picked < oneYearAgo) {
                        const fixed = `${today.getFullYear()}-${m[2]}-${m[3]}`;
                        patch({ dueDate: fixed });
                        return;
                      }
                    }
                    patch({ dueDate: v });
                  }}
                  className="w-full rounded-md border border-white/10 bg-black/20 px-2 py-1 text-xs text-slate-100 focus:border-violet-500/60 focus:outline-none"
                />
                {draft.dueDate && (
                  <button
                    onClick={() => patch({ dueDate: null })}
                    className="mt-1 text-[10px] text-slate-500 hover:text-slate-300"
                  >
                    limpar
                  </button>
                )}
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <Tag className="h-3 w-3" /> Categorias
                  </span>
                  <button
                    onClick={() => setSettingsOpen(true)}
                    className="text-slate-500 hover:text-violet-300"
                    title="Gerenciar categorias"
                  >
                    <Settings className="h-3 w-3" />
                  </button>
                </div>
                {categories.length === 0 ? (
                  <button
                    onClick={() => setSettingsOpen(true)}
                    className="w-full rounded-md border border-dashed border-white/10 p-2 text-[11px] text-slate-500 hover:border-violet-500/40"
                  >
                    Sem categorias ainda. Clique para criar.
                  </button>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {categories.map((cat) => {
                      const active = (draft.categoryIds || []).includes(cat.id);
                      return (
                        <button
                          key={cat.id}
                          onClick={() => toggleCategory(cat.id)}
                          className={clsx(
                            "group inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition",
                            active ? "ring-1 ring-white/30" : "opacity-60 hover:opacity-100",
                          )}
                          style={{
                            backgroundColor: active ? categoryBg(cat.color) : "rgba(255,255,255,0.03)",
                            borderColor: active ? categoryBorder(cat.color) : "rgba(255,255,255,0.1)",
                            color: active ? "#fff" : undefined,
                          }}
                        >
                          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: cat.color }} />
                          {cat.name}
                          {cat.aiProposed && <Sparkles className="h-2.5 w-2.5 opacity-70" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="mt-auto border-t border-white/5 pt-4 text-[10px] text-slate-500">
                Criado {fmtRelative(draft.createdAt)}
                <br />
                Atualizado {fmtRelative(draft.updatedAt)}
                {draft.completedAt && (
                  <>
                    <br />
                    Concluído {fmtRelative(draft.completedAt)}
                  </>
                )}
              </div>
            </aside>
          </div>

          <AnimatePresence>
            {refineOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 backdrop-blur-sm"
                onClick={() => !refining && setRefineOpen(false)}
              >
                <motion.div
                  initial={{ scale: 0.95, y: 8 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.97 }}
                  className="glass w-[480px] max-w-[92vw] rounded-xl p-5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-100">
                    <Wand2 className="h-4 w-4 text-violet-300" /> Refinar com IA
                  </div>
                  <p className="mb-3 text-xs text-slate-400">
                    Descreva em linguagem natural como modificar a tarefa. Ex: "quebre a subtarefa 2 em três passos",
                    "reescreva mais curto", "aumente a prioridade", "marque pra essa sexta".
                  </p>
                  <textarea
                    autoFocus
                    value={refineInstr}
                    onChange={(e) => setRefineInstr(e.target.value)}
                    className="min-h-[100px] w-full resize-y rounded-lg border border-white/10 bg-black/30 p-3 text-sm focus:border-violet-500/60 focus:outline-none"
                    placeholder="Sua instrução…"
                  />
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      onClick={() => setRefineOpen(false)}
                      disabled={refining}
                      className="rounded-lg px-3 py-1.5 text-sm text-slate-300 hover:bg-white/5"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={runRefine}
                      disabled={refining || !refineInstr.trim()}
                      className="flex items-center gap-1.5 rounded-lg bg-violet-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-violet-400 disabled:opacity-40"
                    >
                      {refining ? <IagoSpinner size={14} /> : <Sparkles className="h-3.5 w-3.5" />}
                      {refining ? "Processando" : "Aplicar"}
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>

    <AnimatePresence>
      {viewingAttachment && (
        <AttachmentViewer
          attachment={viewingAttachment}
          onClose={() => setViewingAttachment(null)}
        />
      )}
    </AnimatePresence>
    </>
  );
}
